import { Inject } from '@odinjs/odin';
import { ButtonAPI, FieldListAPI, FormAPI, ModalAPI, PanelAPI, TextAreaAPI } from '@philips-internal/oswald-ui-components';
import { ContextType, Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnClick, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component/context';
import { SubmitEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { showToastNotificationError, showToastNotificationSuccess } from '../../utils/toast-notification-utils';
import UserContext from '../../utils/user-context';
import { checkValidEmailList } from '../../utils/user-util';
import BaseController from '../BaseController';

interface ContextData {
  data: MailParams;
}

interface MailParams {
  emailType: string;
  params: Record<string, Value>;
}

interface ScheduleMailParams {
  ieScheduleType: string;
  nrSeqAgenda: string;
}

@Controller({ component: 'send-mail', fragment: 'send-mail' })
class SendMailModalController extends BaseController {

  @Inject('panel-loader-send-mail')
    panelLoaderSendMail!: PanelAPI;

  @Inject('send-mail-panel')
    sendMailPanel!: PanelAPI;

  @Inject('send-mail-field-notes')
    sendMailFieldNotes!: TextAreaAPI;

  @Inject('field-list-dropdown-send-mail')
    fieldListDropdownSendMail!: FieldListAPI;

  @Inject('send-mail')
    mainModal!: ModalAPI;

  @Inject(ContextType.SUBMITTABLE_FRAGMENT)
    submittableFragmentContext!: SubmittableFragmentContext;

  @Inject('button-send-mail')
    buttonSendMailAPI!: ButtonAPI;

  @Inject('form-dropdown-send-mail')
    formDropdownSendMailAPI!: FormAPI;

  @OnValidate({ target: 'form-dropdown-send-mail' })
  handleFormDropdownSendMailValidate(event: ValidateEvent): void {
    this.buttonSendMailAPI.setEnabled(event.valid);
  }

  @OnClick({ target: ['button-send-mail'] })
  onConfirmSendMailClick(): void {
    interface EmailFromFieldList {
      email: string;
    }

    if (checkValidEmailList(this.fieldListDropdownSendMail.getValue())) {
      const { patientId } = UserContext;
      const mailList = this.fieldListDropdownSendMail.getValue() as unknown as EmailFromFieldList[];
      const context = this.context as unknown as ContextData;
      const mailParams = context.data as MailParams;
      let body;
      let mailEndpoint;
      let endpointParams;
      if (mailParams.emailType === 'report') {
        mailEndpoint = APPLICATION_ENDPOINTS.POST_EXAM_REPORT_MAIL;
        endpointParams = {
          patientId: patientId
        };

        body = {
          mailTo: mailList.map(item => { return item.email}),
          notes: this.sendMailFieldNotes.getValue(),
          reportData: {
            parameters: mailParams.params
          }
        };
      } else {
        const SchedulMailParams = mailParams.params as unknown as ScheduleMailParams;
        mailEndpoint = APPLICATION_ENDPOINTS.SEND_SCHEDULE_ICS_EMAIL;
        endpointParams = { patientId: patientId, ...SchedulMailParams };
        body = {
          mailTo: mailList.map(item => { return item.email}),
          notes: this.sendMailFieldNotes.getValue(),
        };
      }
      this.setLoaderSendMailDropDown(true);
      HttpService.post(mailEndpoint, { ...endpointParams }, { body }).then(() => {
        this.submittableFragmentContext.submit(new SubmitEvent());
        showToastNotificationSuccess('patient.email-sended');
      }).catch(() => {
        showToastNotificationError('newaccount.backend.error.sending.email');
        this.setLoaderSendMailDropDown(false);
      });
    }
  }

  setLoaderSendMailDropDown(showLoader: boolean): void {
    if (showLoader) {
      this.context.set({ formDropdownSendEmailData: this.formDropdownSendMailAPI.getRecord() })
    }

    this.sendMailPanel.setVisible(!showLoader);
    this.panelLoaderSendMail.setVisible(showLoader);

    if (!showLoader) {
      const previousFormData = this.context.get('formDropdownSendEmailData') as Record<string, Value>;
      if (previousFormData) {
        this.formDropdownSendMailAPI.setData(previousFormData);
      }
    }
  }
}

export default SendMailModalController;